import type { ArticleCardDto, ArticleDto, TableArticleDto } from '@/ApiClient';

/**
 * Slim `ArticleDto`s projected from a list row the user just clicked, so a details
 * view can paint the article's identity while its own fetch is still in flight.
 *
 * Only fields the row genuinely carries are filled in — everything else (SKUs,
 * seasonalities, orders) comes back empty. Those empties are NOT data:
 * anything reading them must wait for `useArticles().hasFullArticle(id)`, or it
 * will render "no seasons / no SKUs / no orders" as if it were true.
 */

export function partialArticleFromTableRow(row: TableArticleDto | undefined): ArticleDto | undefined {
  if (!row) return undefined;

  // The table's product images carry `articleIds`; the article's carry `articleIDs`.
  const productImages = row.product.images.map(({ articleIds, ...image }) => ({ ...image, articleIDs: articleIds }));

  return {
    id: row.id,
    articleNumber: row.articleNumber,
    sequentialNumber: 0,
    product: {
      id: row.product.id,
      name: row.product.name,
      hierarchyNodeId: row.product.hierarchyNodeId ?? 0,
      sizeSystemVersionId: row.product.sizeSystemVersionId,
      sizeSystemName: row.product.sizeSystemName,
      images: productImages,
      numberOfArticles: 0,
      seasonalities: [],
      roleAssignments: row.product.roleAssignments,
    },
    hierarchyNodeId: row.product.hierarchyNodeId ?? 0,
    productCategoryId: row.productCategoryId,
    statusId: row.statusId,
    color: row.color ?? undefined,
    genders: row.genders,
    skus: [],
    comment: row.comment,
    description: row.description,
    seasonalities: [],
    images: row.images,
    tariffCodeIds: row.tariffCodeIds,
    dropIds: row.dropIds,
    weight: row.weight,
    orders: [],
    certifications: row.certifications,
    compliances: row.compliances,
    createdAt: row.createdAt,
  };
}

export function partialArticleFromCard(card: ArticleCardDto | undefined): ArticleDto | undefined {
  if (!card) return undefined;

  return {
    id: card.id,
    articleNumber: card.articleNumber,
    sequentialNumber: 0,
    product: {
      id: card.productId,
      name: card.productName,
      hierarchyNodeId: card.hierarchyNodeId ?? 0,
      images: [],
      numberOfArticles: 0,
      seasonalities: [],
      roleAssignments: [],
    },
    hierarchyNodeId: card.hierarchyNodeId ?? 0,
    statusId: card.statusId,
    // The card flattened the colour to name + RGB, which is all the swatch and its label need.
    // Everything that identifies the colour — its id, number, code, group, status, palettes — comes back
    // with the real DTO, so `id: 0` here is a placeholder and never a colour anything can be set to.
    color: card.color?.name
      ? {
          ...card.color,
          id: 0,
          number: '',
          statusId: 0,
          typeId: 0,
          paletteIds: [],
          c: 0,
          m: 0,
          y: 0,
          k: 0,
          libraryAllowsNameOverride: true,
          libraryAllowsCodeOverride: true,
        }
      : undefined,
    genders: card.genders,
    skus: [],
    // A bare URL isn't an `ArticleDtoImage` (no id/dimensions), so the gallery waits.
    images: [],
    seasonalities: [],
    tariffCodeIds: [],
    dropIds: [],
    orders: [],
    certifications: [],
    compliances: [],
  };
}
